import * as C from './consts.js';
import S3Client from './S3Client.js';
import * as U from './utils.js';
/**
 * S3Companion is an S3Client that interacts with a Companion server to perform S3 operations.
 */
class S3Companion extends S3Client {
    companionEndpoint;
    constructor({ companionEndpoint, requestAbortTimeout, }) {
        super({ requestAbortTimeout });
        this.companionEndpoint = companionEndpoint;
    }
    async _fetch(path, opts) {
        const response = await fetch(`${this.companionEndpoint}/s3${path}`, opts);
        if (!response.ok) {
            throw new Error(`Companion request failed: ${response.statusText}`);
        }
        return response;
    }
    async _request({ url, method, data, onProgress, signal, contentType, }) {
        // Wait for online before starting
        await this.waitForOnline(signal);
        return this.xhr({ url, method, data, onProgress, signal, contentType });
    }
    async putObject({ key: requestedKey, data, fileType = C.DEFAULT_STREAM_CONTENT_TYPE, metadata = {}, onProgress, signal, }) {
        const searchParams = new URLSearchParams({
            filename: requestedKey,
            type: fileType,
            ...Object.fromEntries(Object.entries(metadata).map(([k, v]) => [`metadata[${k}]`, String(v)])),
        });
        const response = await this._fetch(`/params?${searchParams}`);
        const { url, fields } = await response.json();
        await this.waitForOnline(signal);
        return this.postObject({ url, fields, data, onProgress, signal });
    }
    async createMultipartUpload({ key: requestedKey, fileType = C.DEFAULT_STREAM_CONTENT_TYPE, metadata, signal, }) {
        if (typeof fileType !== 'string') {
            throw new TypeError(`${C.ERROR_PREFIX}fileType must be a string`);
        }
        const method = 'POST';
        const response = await this._fetch('/multipart', {
            method,
            body: JSON.stringify({
                filename: requestedKey,
                metadata,
                type: fileType,
            }),
            headers: { 'content-type': 'application/json' },
            signal,
        });
        const { key, uploadId, } = await response.json();
        if (uploadId == null)
            throw new Error('No uploadId returned');
        if (key == null)
            throw new Error('No key returned');
        return { uploadId, key };
    }
    async uploadPart({ key, uploadId, data, partNumber, onProgress, signal, }) {
        const response = await this._fetch(`/multipart/${encodeURIComponent(uploadId)}/${encodeURIComponent(partNumber)}?${new URLSearchParams({ key })}`, {
            method: 'GET',
            signal,
        });
        const { url } = await response.json();
        const xhr = await this._request({
            url,
            method: 'PUT',
            data,
            onProgress,
            signal,
        });
        const etag = U.sanitizeETag(xhr.getResponseHeader('etag'));
        if (etag == null) {
            throw new Error(`${C.ERROR_PREFIX}Missing ETag in uploadPart response headers`);
        }
        return { etag };
    }
    async listParts({ uploadId, key, signal, }) {
        if (!uploadId) {
            throw new TypeError(C.ERROR_UPLOAD_ID_REQUIRED);
        }
        const response = await this._fetch(`/multipart/${encodeURIComponent(uploadId)}?${new URLSearchParams({ key })}`, {
            method: 'GET',
            signal,
        });
        const parts = await response.json();
        return parts.map((p) => ({
            partNumber: parseInt(String(p.PartNumber), 10),
            etag: String(p.ETag),
        }));
    }
    async completeMultipartUpload({ key, uploadId, parts, signal, }) {
        const response = await this._fetch(`/multipart/${encodeURIComponent(uploadId)}/complete?${new URLSearchParams({ key })}`, {
            method: 'POST',
            body: JSON.stringify({
                parts: parts.map((part) => ({
                    PartNumber: part.partNumber,
                    ETag: part.etag,
                })),
            }),
            headers: { 'content-type': 'application/json' },
            signal,
        });
        const { location, bucket, key: resultKey, } = await response.json();
        return {
            location,
            bucket,
            key: resultKey,
        };
    }
    async abortMultipartUpload({ key, uploadId, signal, }) {
        await this._fetch(`/multipart/${encodeURIComponent(uploadId)}?${new URLSearchParams({ key })}`, {
            method: 'DELETE',
            signal,
        });
    }
}
export default S3Companion;
